import React, { useState, useEffect } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate, useSearchParams } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import {
  assignTerritoriesToEmployees,
  assignSalesOffices,
  getLmdTerritoryDetails,
  updateLmdTerritoryEmployees,
  updateLmdTerritorySalesOffices,
} from "../api/territory";
import { getDropdownsales } from "../api/auth";
import { getAllLmdTerritories } from "../../../api/territoryMap";
import Dropdown from "../components/Dropdown";
import Popup from "../../../components/AuthPopup";
import Loader from "../../../components/Loader";

const AddNewTerritory = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    emp_ids: [] as string[],
    sales_office_ids: [] as string[],
    territory_id: "",
  });
  const [empIdsInput, setEmpIdsInput] = useState("");
  const [salesOfficeIdsInput, setSalesOfficeIdsInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [lmdTerritories, setLmdTerritories] = useState<any[]>([]);
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();

  // Handle edit mode - pre-fill territory_id from query params and fetch existing data
  useEffect(() => {
    const territoryId = searchParams.get("territory_id");
    if (territoryId) {
      setIsEditMode(true);
      setFormData((prev) => ({
        ...prev,
        territory_id: territoryId,
      }));
      // Fetch existing territory details
      fetchTerritoryDetails(territoryId);
    }
  }, [searchParams]);

  const fetchTerritoryDetails = async (territoryId: string) => {
    try {
      setLoadingData(true);
      const response = await getLmdTerritoryDetails(territoryId);
      
      // Handle different response structures
      let territories = [];
      if (Array.isArray(response)) {
        territories = response;
      } else if (response?.items && Array.isArray(response.items)) {
        territories = response.items;
      } else if (response?.data && Array.isArray(response.data)) {
        territories = response.data;
      } else if (response && !Array.isArray(response)) {
        // Single territory object
        territories = [response];
      }
      
      if (territories.length > 0) {
        // Find the territory with matching territory_id
        const territory = territories.find((t: any) => 
          t.territory_id?.toString() === territoryId || 
          t.territory_id === territoryId
        );
        
        if (territory) {
          // Pre-populate form with existing data
          if (territory.assigned_employees && territory.assigned_employees.length > 0) {
            const empIds = territory.assigned_employees.map((emp: any) => 
              emp.emp_id?.toString() || emp.emp_id
            ).filter((id: any) => id);
            setFormData((prev) => ({
              ...prev,
              emp_ids: empIds,
              sales_office_ids: [],
            }));
            setEmpIdsInput(empIds.join(", "));
            setSalesOfficeIdsInput("");
          } else if (territory.assigned_sales_offices && territory.assigned_sales_offices.length > 0) {
            const salesOfficeIds = territory.assigned_sales_offices.map((office: any) => 
              office.sales_office_id?.toString() || office.sales_office_id
            ).filter((id: any) => id);
            setFormData((prev) => ({
              ...prev,
              sales_office_ids: salesOfficeIds,
              emp_ids: [],
            }));
            setSalesOfficeIdsInput(salesOfficeIds.join(", "));
            setEmpIdsInput("");
          }
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch territory details:", error);
      if (error?.response?.status === 404) {
        showErrorModal("Territory details not found");
      } else if (error?.response?.status === 500) {
        showErrorModal("Error while fetching territory details");
      } else {
        const detail = error?.response?.data?.detail;
        showDetailErrors(detail, "An unexpected error occurred while fetching territory details.");
      }
    } finally {
      setLoadingData(false);
    }
  };

  const showErrorModal = (message: string) => {
    setPopupMessage(message || "An unexpected error occurred.");
    setShowPopup(true);
  };

  const showDetailErrors = (detail: any, fallback?: string) => {
    if (Array.isArray(detail) && detail.length > 0) {
      const combined = detail
        .map((err: any) => err?.msg || err?.message || String(err))
        .join("\n");
      showErrorModal(combined);
      return;
    }
    showErrorModal(detail?.msg || detail || fallback || "An unexpected error occurred.");
  };
  const handleChangeDropdown = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
  };
  
  const getLabelClass = (disabled: boolean) =>
    `block font-semibold mb-1 ${disabled ? "text-gray-400" : "text-gray-700"}`;
  
  const territoryOptions = lmdTerritories.map((territory) => ({
    value: territory.territory_id?.toString() || "",
    name: territory.territory_name || `Territory ${territory.territory_id}`,
  }));
  
  const isTerritoryDisabled = territoryOptions.length === 0;
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // Make emp_ids and sales_office_ids mutually exclusive
    if (name === "emp_ids") {
      setEmpIdsInput(value);
      // Parse comma-separated values into array, trim whitespace, and filter empty strings
      const empIdsArray = value
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      setFormData({ ...formData, emp_ids: empIdsArray, sales_office_ids: [] });
      setSalesOfficeIdsInput("");
    } else if (name === "sales_office_ids") {
      setSalesOfficeIdsInput(value);
      // Parse comma-separated values into array, trim whitespace, and filter empty strings
      const salesOfficeIdsArray = value
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      setFormData({ ...formData, sales_office_ids: salesOfficeIdsArray, emp_ids: [] });
      setEmpIdsInput("");
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      
      // Validate that either emp_ids or sales_office_ids is provided, but not both
      if (formData.emp_ids.length === 0 && formData.sales_office_ids.length === 0) {
        setInvalidFields(["emp_ids", "sales_office_ids"]);
        showErrorModal("Please provide either Employee ID(s) or Sales Office ID(s).");
        return;
      }

      if (formData.emp_ids.length > 0 && formData.sales_office_ids.length > 0) {
        setInvalidFields(["emp_ids", "sales_office_ids"]);
        showErrorModal("Please provide only one: either Employee ID(s) or Sales Office ID(s), not both.");
        return;
      }

      // Validate territory_id is provided
      if (!formData.territory_id) {
        setInvalidFields(["territory_id"]);
        showErrorModal("Please select a territory.");
        return;
      }

      setInvalidFields([]);

      let response;
      const basePayload = {
        territory_id: formData.territory_id,
      };

      // Use PUT endpoints for edit mode, POST endpoints for create mode
      if (isEditMode) {
        // Update mode - use PUT endpoints
        if (formData.emp_ids.length > 0) {
          const payload = {
            emp_ids: formData.emp_ids,
          };
          response = await updateLmdTerritoryEmployees(formData.territory_id, payload);
        } else if (formData.sales_office_ids.length > 0) {
          const payload = {
            sales_office_ids: formData.sales_office_ids,
          };
          response = await updateLmdTerritorySalesOffices(formData.territory_id, payload);
        }
      } else {
        // Create mode - use POST endpoints
        if (formData.emp_ids.length > 0) {
          const payload = {
            ...basePayload,
            emp_ids: formData.emp_ids,
          };
          response = await assignTerritoriesToEmployees(payload);
        } else if (formData.sales_office_ids.length > 0) {
          const payload = {
            ...basePayload,
            sales_office_ids: formData.sales_office_ids,
          };
          response = await assignSalesOffices(payload);
        }
      }

      if (!response) {
        showErrorModal("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        return;
      }
      toast.success(response?.message);
      setTimeout(() => {
        navigate(URL_MAPPING.LMDTERRITORY);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to assign territory:", error);
      if (error?.response?.status === 404) {
        showErrorModal("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        showErrorModal(
          error?.response?.data?.detail || "Error while assigning the territory"
        );
      } else {
        const detail = error?.response?.data?.detail;
        showDetailErrors(detail);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchLmdTerritories = async () => {
      try {
        const res = await getAllLmdTerritories();
        if (!res || !Array.isArray(res)) {
          console.warn("Failed to load LMD territories");
          setLmdTerritories([]);
          return;
        }
        setLmdTerritories(res);
      } catch (error: any) {
        console.error("Failed to fetch LMD territories:", error);
        if (error?.response?.status === 404) {
          showErrorModal("LMD territories not found, Please try again later");
        } else if (error?.response?.status === 500) {
          showErrorModal("Error while fetching LMD territories");
        } else {
          const detail = error?.response?.data?.detail;
          showDetailErrors(detail, "An unexpected error occurred while fetching territories.");
        }
        setLmdTerritories([]);
      }
    };

    const fetchSalesOffices = async () => {
      try {
        const res = await getDropdownsales();
        if (!res) {
          console.warn("Failed to load sales offices");
          setSalesOffices([]);
          return;
        }
        setSalesOffices(res.sales_offices || []);
      } catch (error: any) {
        console.error("Failed to fetch sales offices:", error);
        setSalesOffices([]);
      }
    };

    fetchLmdTerritories();
    fetchSalesOffices();
  }, []);
  return (
    <LayerAdmin>
      <Popup
        isOpen={showPopup}
        title="Error"
        setShowPopup={setShowPopup}
      >
        <p className="text-sm text-gray-600 whitespace-pre-line">
          {popupMessage}
        </p>
      </Popup>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">
          {isEditMode ? "Edit Territory Assignment" : "Assign Territory"}
        </h3>
      </div>
      {loadingData ? (
        <div className="flex justify-center items-center p-8">
          <Loader />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1">Employee ID(s) *</label>
          <input
            type="text"
            name="emp_ids"
            value={empIdsInput}
            onChange={handleChange}
            className={getInputClass("emp_ids")}
            placeholder="Enter Employee IDs (comma-separated, e.g., 001, 002, 003)"
            disabled={formData.sales_office_ids.length > 0}
          />
          {formData.sales_office_ids.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Clear Sales Office ID(s) to enter Employee ID(s)
            </p>
          )}
          {formData.emp_ids.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {formData.emp_ids.length} employee ID(s) entered: {formData.emp_ids.join(", ")}
            </p>
          )}
        </div>
        <div className="my-2">
          <label className="block font-semibold mb-1">Sales Office ID(s) *</label>
          <input
            type="text"
            name="sales_office_ids"
            value={salesOfficeIdsInput}
            onChange={handleChange}
            className={getInputClass("sales_office_ids")}
            placeholder="Enter Sales Office IDs (comma-separated, e.g., SO001, SO002, SO003)"
            disabled={formData.emp_ids.length > 0}
          />
          {formData.emp_ids.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Clear Employee ID(s) to enter Sales Office ID(s)
            </p>
          )}
          {formData.sales_office_ids.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {formData.sales_office_ids.length} sales office ID(s) entered: {formData.sales_office_ids.join(", ")}
            </p>
          )}
        </div>
        <div className="my-2">
          <label className={getLabelClass(isTerritoryDisabled)}>
            Territory *
          </label>
          <Dropdown
            name="territory_id"
            value={formData.territory_id}
            onChange={handleChangeDropdown}
            options={territoryOptions}
            className={getInputClass("territory_id")}
            placeholder="Select Territory"
            searchable={true}
            disabled={isTerritoryDisabled || isEditMode}
          />
          {isEditMode && (
            <p className="text-xs text-gray-500 mt-1">
              Territory cannot be changed in edit mode
            </p>
          )}
        </div>

        
        <button
          type="submit"
          disabled={loading || loadingData}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isEditMode ? "Update" : "Assign"} {loading && <ButtonLoader />}
        </button>
      </form>
      )}
    </LayerAdmin>
  );
};

export default AddNewTerritory;
